import { useCallback, useContext, useEffect, useState } from "react";
import { type Control, useWatch } from "react-hook-form";

import { Stack } from "@mui/material";

import {
  FormType,
  isStructuredAddressComplete,
  mustDisplayAddressQuestion,
  QuestionType,
  serializeStructuredAddress,
} from "@simplyk/common";

import { useFormV2Context } from "../../../contexts/FormV2Context";
import { usePrefillSearchParams } from "../../../helpers/usePrefillSearchParams";
import { getBuyerPrefillFromParticipants } from "../../../helpers/getBuyerPrefillFromParticipants";
import { useTrackPrefillEdited } from "../../../helpers/useTrackPrefillEdited";
import { QuestionFieldNotificationPhone } from "../../questions/QuestionField/QuestionFieldNotificationPhone";
import { GiveAsSection } from "../GiveAsSection/GiveAsSection";
import { useGiveAsSectionRenderPosition } from "../GiveAsSection/useGiveAsSectionRenderPosition";
import { PublicDonationCheckbox, usePublicDonationCheckboxRenderPosition } from "../PublicDonationCheckBox";
import { SectionWrapper } from "../SectionWrapper";

import { AuctionAnonymousBidCheckbox, useAuctionAnonymousBidCheckboxShouldRender } from "./AuctionAnonymousBidCheckbox";
import { BuyerInformationAddress } from "./BuyerInformationAddress/BuyerInformationAddress";
import { StripeAddressElement } from "./StripeAddressElement/StripeAddressElement";

import { BuyerInformationSectionLayout } from "@/canvas/features/FormV2/blocks/payment/BuyerInformation/BuyerInformationSectionLayout";
import { FrontendFormContext } from "@/common/contexts/FrontendFormContext";
import { isEmailAndAllowPlus, isRequired } from "@/common/helpers/validators";
import type { TicketingPaymentInput } from "@/common/types/ticketing";
import { FormCheckbox } from "@/forms-design-system/FormCheckbox";
import { useDefaultCountryRegion } from "@/common/hooks/useDefaultCountryRegion";
import { useTranslate } from "@/common/hooks/useTranslate";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { usePreviewContext } from "@/features/LiveFormEditor/LivePreview/contexts/PreviewContext";
import { FormTextField } from "@/forms-design-system/FormTextField/FormTextField";

export const BuyerInformationSection = () => {
  const { t } = useTranslate();
  const { organization, displayExpressCheckout, formData } = useContext(FrontendFormContext);

  const { form, type, displayGenerateReceipt, questions } = useFormV2Context("common");

  const { setValue, watch, control } = form;
  const { isPreview } = usePreviewContext();

  const countryValue = watch("country");
  const hasGiftAidConfirmation = watch("hasGiftAidConfirmation");
  const { defaultCountry, defaultRegion } = useDefaultCountryRegion(organization);
  const publicDonationCheckboxPosition = usePublicDonationCheckboxRenderPosition();
  const giveAsSectionRenderPosition = useGiveAsSectionRenderPosition();
  const shouldRenderAuctionAnonymousBid = useAuctionAnonymousBidCheckboxShouldRender();

  const {
    contactInfo: { country: urlCountry, region: urlRegion },
  } = usePrefillSearchParams();

  useEffect(() => {
    if (!urlCountry) {
      setValue("country", defaultCountry);
    }
  }, [defaultCountry, setValue, urlCountry]);

  useEffect(() => {
    if (!countryValue) {
      return;
    }
    if (countryValue === defaultCountry) {
      if (!urlRegion) {
        setValue("region", defaultRegion);
      }
    } else if (countryValue !== urlCountry) {
      setValue("region", "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryValue]);

  const shouldDisplayAddressQuestion = mustDisplayAddressQuestion({
    displayGenerateReceipt,
    hasGiftAidConfirmation,
    organization,
    formData,
  });
  const attendeeAddressQuestion = questions.some(
    (question) => question.hasRate && question.type === QuestionType.Address
  );
  const participantAnswers = useWatch({
    control: control as unknown as Control<TicketingPaymentInput>,
    name: "customAnswersOfParticipants",
  });
  const firstParticipantAddress = getBuyerPrefillFromParticipants(participantAnswers, questions).address;
  const [reuseFirstParticipantAddress, setReuseFirstParticipantAddress] = useState(false);
  const canReuseFirstParticipantAddress =
    attendeeAddressQuestion &&
    firstParticipantAddress !== undefined &&
    isStructuredAddressComplete(firstParticipantAddress);
  const reusedAddressKey = firstParticipantAddress ? serializeStructuredAddress(firstParticipantAddress) : null;

  useEffect(() => {
    if (!canReuseFirstParticipantAddress) {
      setReuseFirstParticipantAddress(false);
    }
  }, [canReuseFirstParticipantAddress]);

  const { trackEmailEdited } = useTrackPrefillEdited();

  const orderQuestions = questions.filter((question) => !question.hasRate);
  const notificationPhoneIndex = orderQuestions.findIndex(
    (question) => question.type === QuestionType.NotificationPhoneNumber
  );
  const notificationPhoneSlot =
    !displayExpressCheckout && notificationPhoneIndex !== -1 ? (
      <QuestionFieldNotificationPhone
        name={`customAnswersOfOrder.${notificationPhoneIndex}`}
        question={orderQuestions[notificationPhoneIndex]}
      />
    ) : undefined;

  const handleEmailBlur = useCallback(() => {
    trackEmailEdited();
  }, [trackEmailEdited]);

  return (
    <SectionWrapper
      title={t("ticketing", type === FormType.DonationForm ? "yourInformation" : "buyerInformation")}
      dataTest="buyer-information-section"
    >
      <BuyerInformationSectionLayout
        slots={{
          email: (
            <FormTextField
              control={control}
              name="email"
              rules={[
                isRequired(t("ticketing", "emailRequired")),
                isEmailAndAllowPlus(t("common", "emailFormat"), t("common", "emailFormat.wrongChar")),
              ]}
              type="email"
              label={t("ticketing", "email")}
              data-test="form-email"
              onBlur={handleEmailBlur}
              disableInjectingGlobalStyles={isPreview}
              error={Boolean(form.formState.errors.email?.message)}
              helperText={form.formState.errors.email?.message}
              required
            />
          ),
          publicDonation:
            publicDonationCheckboxPosition === "your-information-section" ? <PublicDonationCheckbox /> : undefined,
          addressAndName: shouldDisplayAddressQuestion ? (
            <Stack gap={2}>
              {canReuseFirstParticipantAddress && (
                <FormCheckbox
                  size="medium"
                  checked={reuseFirstParticipantAddress}
                  onChange={(_, checked) => setReuseFirstParticipantAddress(checked)}
                  label={t("ticketing", "buyerAddress.sameAsFirstParticipant")}
                  data-test="buyer-address-same-as-first-participant"
                />
              )}
              <StripeAddressElement
                key={reuseFirstParticipantAddress ? reusedAddressKey || "first-participant" : "independent"}
                addressPrefill={reuseFirstParticipantAddress ? firstParticipantAddress : undefined}
                defaultCountry={defaultCountry}
              />
            </Stack>
          ) : (
            <BuyerInformationAddress />
          ),
          notificationPhone: notificationPhoneSlot,
          organizationDonation: giveAsSectionRenderPosition === "buyer-information" && (
            <GiveAsSection form={form} amplitudePayload={{}} formType={type} />
          ),
          auctionAnonymousBid: shouldRenderAuctionAnonymousBid ? <AuctionAnonymousBidCheckbox /> : undefined,
        }}
      />
    </SectionWrapper>
  );
};
